import * as React from 'react'
import { KeyRound, Loader2, TriangleAlert } from 'lucide-react'
import type { CreatedApiKeyDto } from '@lib/dto/tenant-settings.dto'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import { createApiKeyInputSchema, type CreateApiKeyInput } from '@lib/input/tenant-settings.input'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { SecretValue } from './SecretValue'

const MODE_DESCRIPTIONS: Record<TenantMode, string> = {
  live: "Live keys create real envelopes that send real email. Every live key shares this organization's live webhook and embed settings.",
  test: "Test keys create test envelopes: the same API, separate data, and nothing legally binding. Every test key shares this organization's test webhook and embed settings.",
}

interface CreateKeyDialogProps {
  mode: TenantMode
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (input: CreateApiKeyInput) => Promise<CreatedApiKeyDto>
}

/**
 * Names a new key in `mode`, then shows its secret, once. Closing the dialog
 * is the last chance to copy it; the dashboard can never show it again.
 */
export function CreateKeyDialog({ mode, open, onOpenChange, onCreate }: CreateKeyDialogProps) {
  const [name, setName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isPending, setIsPending] = React.useState(false)
  const [created, setCreated] = React.useState<CreatedApiKeyDto | null>(null)

  function close(): void {
    setName('')
    setError(null)
    setCreated(null)
    onOpenChange(false)
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = createApiKeyInputSchema.safeParse({ name, mode })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Give the key a name')
      return
    }
    setError(null)
    setIsPending(true)
    try {
      setCreated(await onCreate(parsed.data))
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not create the key.'))
    } finally {
      setIsPending(false)
    }
  }

  function renderSecret(result: CreatedApiKeyDto) {
    return (
      <div className="space-y-4">
        <DialogHeader>
          <DialogTitle>Copy your new key</DialogTitle>
          <DialogDescription>“{result.key.name}” is ready. Store it in your server&apos;s secrets now.</DialogDescription>
        </DialogHeader>
        <SecretValue value={result.secret} label="API key" />
        <p className="flex items-start gap-2 text-body text-warning">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          This is the only time the full key is shown. If you lose it, create a new key and revoke this one.
        </p>
        <DialogFooter>
          <Button onClick={close}>I&apos;ve saved it</Button>
        </DialogFooter>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        {created ? (
          renderSecret(created)
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <DialogHeader>
              <DialogTitle>Create a {mode} key</DialogTitle>
              <DialogDescription>{MODE_DESCRIPTIONS[mode]}</DialogDescription>
            </DialogHeader>
            <Field label="Name" htmlFor="key-name" error={error} hint="Where it's used, e.g. “Loan servicing API”.">
              <Input
                id="key-name"
                autoComplete="off"
                value={name}
                aria-invalid={error ? true : undefined}
                aria-describedby={fieldMessageId('key-name')}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="animate-spin" /> : <KeyRound />}
                Create key
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
